"use client"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { usePathname } from "next/navigation"
import React, { useEffect, useState } from "react"
import { getNonUmbracoUrl, getUmbracoMediaUrl } from "utils"
import { transformProperties } from "utils/simplifyData"
import ButtonWithArrow, {
  colorPalletteIndex,
  colorPalletteIndexType,
} from "../common/bit/ButtonWithArrow"
import { useIsDesktop } from "../common/bit/MediaQueries"
import { colors, mediaQueries, sizes } from "../theme/theme"

export interface ActiveItem {
  colorPallette: string
  link: string
  Icon: any
  text: string
  adamTotalID?: string
}

interface IconsLinksListProps {
  itemsList: Array<{
    properties: {
      Icon: {
        mediaItems: Array<{
          url: string
        }>
      }
      text: {
        value: string
      }
      link: {
        links: Array<{
          url: string
        }>
      }
      adamTotalID?: {
        value: string
      }
    }
  }>
  setActive?: (activeItem: Partial<ActiveItem>) => void
}

const IconsListWrapper = styled(Box)`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  z-index: 10;
  margin-top: -120px;
  ${mediaQueries.tabletDown} {
    flex-flow: column nowrap;
    margin-top: 0;
    gap: 0;
    overflow-y: auto;
  }
  ${mediaQueries.desktopXl} {
    gap: 15px;
  }
`

export const IconWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: ${sizes.baseSingle};
  ${mediaQueries.tabletDown} {
    padding: 0;
  }
  ${mediaQueries.desktopXl} {
    padding-bottom: ${sizes.baseDouble};
  }
`

export const IconCard = styled(ButtonWithArrow)`
  &.MuiButtonBase-root {
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: ${sizes.radius.medium};
    background-color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    font-size: 16px;
    width: 126px;
    height: 126px;
    &:hover,
    &:focus,
    &.active {
      background-color: ${colors.white};
    }
    svg {
      display: none;
    }
    ${mediaQueries.tabletDown} {
      flex-direction: row;
      justify-content: flex-start;
      width: 100%;
      height: initial;
      box-shadow: none;
      padding: 19px 12px;
      gap: ${sizes.baseSingle};
      border-radius: ${sizes.baseSingle};
      border-bottom: 1px solid ${colors.lightGray};
      &:hover,
      &:focus,
      &.active {
        background-color: ${colors.white};
        box-shadow: ${colors.dropShadow.main};
      }
      svg {
        display: flex;
        height: 32px;
        width: 32px;
        margin-inline-start: auto;
      }
    }
    ${mediaQueries.desktopXl} {
      width: 160px;
      height: 148px;
    }
  }
`

export const CardTitle = styled(Typography)<{ component?: string }>`
  display: flex;
  color: ${colors.black};
  font-weight: 700;
  text-align: center;
  font-size: 16px;
  line-height: 19px;
  align-items: center;
  ${mediaQueries.desktopM} {
    font-size: 14px;
  }
  ${mediaQueries.desktopXl} {
    font-size: 16px;
  }
`

const IconsLinksList: React.FC<IconsLinksListProps> = ({ itemsList }) => {
  const pathname = usePathname()
  const isDesktop = useIsDesktop()
  const [mounted, setMounted] = useState(false)
  const t = useTranslations("InfoFormsList")

  // Try to get JobsContext, if available
  let jobsContext
  try {
    // Dynamically import useJobsContext to avoid errors on pages without JobsContext
    const {
      useJobsContext,
    } = require("../../app/[locale]/career/jobs/JobsContext")
    jobsContext = useJobsContext()
  } catch (error) {
    // JobsContext not available, continue without it
    jobsContext = null
  }

  // Wait until after client-side hydration to enable className matching
  useEffect(() => {
    setMounted(true)
  }, [])

  // Update JobsContext categories in useEffect, not during render
  useEffect(() => {
    if (jobsContext && itemsList) {
      if (itemsList.length > 0 && jobsContext.setCategoriesItems) {
        jobsContext.setCategoriesItems(itemsList)
      }
    }
  }, [jobsContext, itemsList])

  return (
    <IconsListWrapper>
      {itemsList &&
        itemsList?.map((item, index) => {
          const { Icon, text, link, adamTotalID } = transformProperties(
            item?.properties,
          )
          const colorPallette = colorPalletteIndex[
            index % colorPalletteIndex.length
          ] as colorPalletteIndexType
          const currLink = getNonUmbracoUrl(link?.links[0]?.url)
          const isActive =
            currLink.replace(/\/$/, "") === pathname.replace(/\/$/, "")

          return (
            <IconCard
              key={`${currLink}-${text.value}-${index}`}
              variant="elevated"
              colorPallette={colorPallette || "turquoise"}
              ArrowWithLine={false}
              // Only add active class after client-side hydration
              className={mounted && isActive ? "active" : ""}
              href={currLink}
              data-adam-total-id={adamTotalID?.value}
            >
              {Icon && Icon?.mediaItems && (
                <IconWrapper>
                  <Image
                    src={getUmbracoMediaUrl(Icon?.mediaItems[0]?.url)}
                    alt={`${t("iconAlt")}${text.value}`}
                    width={isDesktop ? 48 : 32}
                    height={isDesktop ? 48 : 32}
                  />
                </IconWrapper>
              )}
              <CardTitle variant="h6" component="p">
                {text.value}
              </CardTitle>
            </IconCard>
          )
        })}
    </IconsListWrapper>
  )
}

export default IconsLinksList
